import React from 'react';
import {
  Sparkles,
  Compass,
  Moon,
  Sun,
  Crown,
  Zap,
  Heart,
  Flame,
  Shield,
  Scale,
  Eye,
  Key,
  Feather,
  Infinity as InfinityIcon,
  RotateCw,
  Droplets,
  HeartHandshake,
  HeartCrack,
  Users,
  Coins,
  Home,
  Unlock,
  Bell,
  Globe,
  Wand2,
  Flower2,
  Waves,
  Sword,
  LucideIcon,
} from 'lucide-react';
import { TarotCard } from '../data/tarotDeck';

export interface GlyphMetadata {
  numeral: string;
  archetype: string;
  element: string;
  glyph: LucideIcon;
  companionGlyph?: LucideIcon;
  auraColor: string;
  essence: string;
}

const glyphMap: Record<number, GlyphMetadata> = {
  0: {
    numeral: '0',
    archetype: 'The Seeker',
    element: 'Air · Infinite Void',
    glyph: Feather,
    companionGlyph: Compass,
    auraColor: 'from-sky-500/25 via-lavender-200/20 to-purple-600/20',
    essence: 'Leap of Faith',
  },
  1: {
    numeral: 'I',
    archetype: 'The Channel',
    element: 'Mercury · Manifestation',
    glyph: Wand2,
    companionGlyph: InfinityIcon,
    auraColor: 'from-amber-400/30 via-lavender-200/25 to-purple-600/25',
    essence: 'As Above, So Below',
  },
  2: {
    numeral: 'II',
    archetype: 'The Oracle',
    element: 'Moon · Sacred Intuition',
    glyph: Moon,
    companionGlyph: Eye,
    auraColor: 'from-indigo-500/30 via-blue-400/20 to-purple-500/25',
    essence: 'Inner Knowing',
  },
  3: {
    numeral: 'III',
    archetype: 'The Matrix',
    element: 'Venus · Fertile Creation',
    glyph: Crown,
    companionGlyph: Flower2,
    auraColor: 'from-emerald-400/25 via-lavender-200/25 to-rose-400/20',
    essence: 'Nurtured Abundance',
  },
  4: {
    numeral: 'IV',
    archetype: 'The Architect',
    element: 'Aries · Foundation & Will',
    glyph: Shield,
    companionGlyph: Flame,
    auraColor: 'from-red-500/25 via-amber-500/25 to-lavender-200/20',
    essence: 'Divine Order',
  },
  5: {
    numeral: 'V',
    archetype: 'The Hierophant',
    element: 'Taurus · Ancient Counsel',
    glyph: Key,
    companionGlyph: Sparkles,
    auraColor: 'from-amber-400/30 via-lavender-200/25 to-yellow-600/20',
    essence: 'Sacred Tradition',
  },
  6: {
    numeral: 'VI',
    archetype: 'The Conjunction',
    element: 'Gemini · Twin Flame',
    glyph: Heart,
    companionGlyph: Sparkles,
    auraColor: 'from-rose-500/30 via-pink-400/20 to-lavender-200/25',
    essence: 'Harmonious Choice',
  },
  7: {
    numeral: 'VII',
    archetype: 'The Charioteer',
    element: 'Cancer · Triumphant Focus',
    glyph: Compass,
    companionGlyph: Shield,
    auraColor: 'from-cyan-500/25 via-blue-600/25 to-lavender-200/20',
    essence: 'Unyielding Will',
  },
  8: {
    numeral: 'VIII',
    archetype: 'The Tamer',
    element: 'Leo · Quiet Sovereignty',
    glyph: InfinityIcon,
    companionGlyph: Heart,
    auraColor: 'from-amber-400/30 via-lavender-200/25 to-rose-500/20',
    essence: 'Grace & Fortitude',
  },
  9: {
    numeral: 'IX',
    archetype: 'The Solitary',
    element: 'Virgo · Inner Light',
    glyph: Flame,
    companionGlyph: Eye,
    auraColor: 'from-amber-300/30 via-lavender-200/20 to-indigo-500/20',
    essence: 'Guiding Beacon',
  },
  10: {
    numeral: 'X',
    archetype: 'The Axis',
    element: 'Jupiter · Cosmic Rhythm',
    glyph: RotateCw,
    companionGlyph: Sparkles,
    auraColor: 'from-purple-500/30 via-lavender-200/25 to-blue-500/20',
    essence: 'Karmic Evolution',
  },
  11: {
    numeral: 'XI',
    archetype: 'The Arbiter',
    element: 'Libra · Absolute Equilibrium',
    glyph: Scale,
    companionGlyph: Sparkles,
    auraColor: 'from-cyan-400/25 via-sky-500/20 to-lavender-200/25',
    essence: 'Cosmic Truth',
  },
  12: {
    numeral: 'XII',
    archetype: 'The Initiate',
    element: 'Neptune · Sacred Suspension',
    glyph: Droplets,
    companionGlyph: Eye,
    auraColor: 'from-teal-400/25 via-indigo-500/25 to-violet-500/20',
    essence: 'Shifted Perspective',
  },
  13: {
    numeral: 'XIII',
    archetype: 'The Chrysalis',
    element: 'Scorpio · Grand Metamorphosis',
    glyph: Flower2,
    companionGlyph: Sparkles,
    auraColor: 'from-violet-600/30 via-purple-400/20 to-rose-500/20',
    essence: 'Death & Rebirth',
  },
  14: {
    numeral: 'XIV',
    archetype: 'The Alchemist',
    element: 'Sagittarius · Dynamic Balance',
    glyph: Waves,
    companionGlyph: Droplets,
    auraColor: 'from-cyan-400/25 via-blue-500/20 to-lavender-200/25',
    essence: 'Soul Synthesis',
  },
  15: {
    numeral: 'XV',
    archetype: 'The Shadow',
    element: 'Capricorn · Unbound Liberation',
    glyph: Unlock,
    companionGlyph: Flame,
    auraColor: 'from-rose-500/25 via-purple-700/30 to-amber-500/20',
    essence: 'Severing Bonds',
  },
  16: {
    numeral: 'XVI',
    archetype: 'The Revelation',
    element: 'Mars · Sudden Illumination',
    glyph: Zap,
    companionGlyph: Sparkles,
    auraColor: 'from-amber-500/35 via-red-500/30 to-lavender-200/30',
    essence: 'Breakthrough Truth',
  },
  17: {
    numeral: 'XVII',
    archetype: 'The Guiding Star',
    element: 'Aquarius · Celestial Hope',
    glyph: Sparkles,
    companionGlyph: Compass,
    auraColor: 'from-cyan-400/35 via-blue-400/25 to-lavender-200/30',
    essence: 'Purity & Faith',
  },
  18: {
    numeral: 'XVIII',
    archetype: 'The Dreamer',
    element: 'Pisces · Twilight Intuition',
    glyph: Moon,
    companionGlyph: Droplets,
    auraColor: 'from-indigo-500/35 via-violet-600/25 to-blue-400/20',
    essence: 'Subconscious Depths',
  },
  19: {
    numeral: 'XIX',
    archetype: 'The Illuminator',
    element: 'Sun · Sovereign Vitality',
    glyph: Sun,
    companionGlyph: Sparkles,
    auraColor: 'from-amber-400/40 via-yellow-400/30 to-lavender-200/35',
    essence: 'Radiant Triumph',
  },
  20: {
    numeral: 'XX',
    archetype: 'The Awakening',
    element: 'Pluto · Higher Calling',
    glyph: Bell,
    companionGlyph: Sparkles,
    auraColor: 'from-purple-500/30 via-indigo-400/25 to-lavender-200/25',
    essence: 'Spiritual Rebirth',
  },
  21: {
    numeral: 'XXI',
    archetype: 'The Ouroboros',
    element: 'Saturn · Cosmic Wholeness',
    glyph: Globe,
    companionGlyph: Sparkles,
    auraColor: 'from-lavender-200/35 via-purple-500/25 to-cyan-400/25',
    essence: 'Full Integration',
  },
  22: {
    numeral: 'ACE',
    archetype: 'Wellspring of Hearts',
    element: 'Water · Primordial Love',
    glyph: Droplets,
    companionGlyph: Heart,
    auraColor: 'from-cyan-400/30 via-rose-400/25 to-blue-500/20',
    essence: 'Overflowing Grace',
  },
  23: {
    numeral: 'II',
    archetype: 'The Sacred Meeting',
    element: 'Water · Two Souled',
    glyph: HeartHandshake,
    companionGlyph: Droplets,
    auraColor: 'from-rose-400/30 via-pink-500/25 to-lavender-200/25',
    essence: 'Divine Partnership',
  },
  24: {
    numeral: 'III',
    archetype: 'The Celebration',
    element: 'Water · Shared Communion',
    glyph: Users,
    companionGlyph: Sparkles,
    auraColor: 'from-rose-400/25 via-amber-400/25 to-purple-400/20',
    essence: 'Sisterhood & Joy',
  },
  25: {
    numeral: 'ACE',
    archetype: 'Seed of the Earth',
    element: 'Earth · Golden Manifestation',
    glyph: Coins,
    companionGlyph: Sparkles,
    auraColor: 'from-amber-400/35 via-emerald-500/25 to-lavender-200/30',
    essence: 'Tangible Wealth',
  },
  26: {
    numeral: 'V',
    archetype: 'The Winter Refuge',
    element: 'Earth · Spiritual Shelter',
    glyph: Heart,
    companionGlyph: Coins,
    auraColor: 'from-amber-500/20 via-indigo-600/25 to-sky-400/20',
    essence: 'Sanctuary Ahead',
  },
  27: {
    numeral: 'ACE',
    archetype: 'Blade of Truth',
    element: 'Air · Pure Intellect',
    glyph: Sword,
    companionGlyph: Zap,
    auraColor: 'from-cyan-400/35 via-sky-400/25 to-blue-600/20',
    essence: 'Decisive Insight',
  },
  28: {
    numeral: 'III',
    archetype: 'The Healing Heart',
    element: 'Air · Catharsis',
    glyph: HeartCrack,
    companionGlyph: Droplets,
    auraColor: 'from-indigo-500/30 via-rose-500/25 to-violet-600/20',
    essence: 'Transcending Grief',
  },
  29: {
    numeral: 'ACE',
    archetype: 'Primordial Fire',
    element: 'Fire · Creative Spark',
    glyph: Flame,
    companionGlyph: Wand2,
    auraColor: 'from-amber-500/35 via-orange-500/30 to-lavender-200/30',
    essence: 'Pure Vital Force',
  },
  30: {
    numeral: 'IV',
    archetype: 'The Pavilion',
    element: 'Fire · Community Haven',
    glyph: Home,
    companionGlyph: Sparkles,
    auraColor: 'from-amber-400/30 via-yellow-400/25 to-rose-400/20',
    essence: 'Joyful Homecoming',
  },
  31: {
    numeral: 'X',
    archetype: 'The Rainbow Arc',
    element: 'Water · Serene Harmony',
    glyph: Heart,
    companionGlyph: Sun,
    auraColor: 'from-rose-400/30 via-sky-400/25 to-lavender-200/25',
    essence: 'Emotional Wholeness',
  },
  32: {
    numeral: 'X',
    archetype: 'The Sacred Ancestry',
    element: 'Earth · Lasting Legacy',
    glyph: Coins,
    companionGlyph: Home,
    auraColor: 'from-amber-400/30 via-emerald-500/25 to-lavender-200/25',
    essence: 'Enduring Abundance',
  },
  33: {
    numeral: 'QUEEN',
    archetype: 'The Solar Queen',
    element: 'Fire · Magnetic Sovereign',
    glyph: Crown,
    companionGlyph: Flame,
    auraColor: 'from-amber-400/35 via-orange-500/25 to-lavender-200/30',
    essence: 'Warm Radiance',
  },
  34: {
    numeral: 'KING',
    archetype: 'The Sovereign Mind',
    element: 'Air · Absolute Clarity',
    glyph: Crown,
    companionGlyph: Sword,
    auraColor: 'from-cyan-400/35 via-indigo-500/25 to-lavender-200/25',
    essence: 'Master of Truth',
  },
};

const defaultGlyph: GlyphMetadata = {
  numeral: '✦',
  archetype: 'The Archetype',
  element: 'Cosmic Prana',
  glyph: Sparkles,
  companionGlyph: Moon,
  auraColor: 'from-lavender-200/30 via-purple-500/25 to-indigo-600/20',
  essence: 'Inner Wisdom',
};

const RANK_NUMERALS: Record<number, string> = {
  1: 'ACE', 2: 'II', 3: 'III', 4: 'IV', 5: 'V', 6: 'VI', 7: 'VII', 8: 'VIII', 9: 'IX', 10: 'X',
  11: 'PAGE', 12: 'KNIGHT', 13: 'QUEEN', 14: 'KING',
};

type Suit = NonNullable<TarotCard['suit']>;

const SUIT_STYLE: Record<
  Suit,
  { element: string; realm: string; glyph: LucideIcon; companionGlyph: LucideIcon; auraColor: string }
> = {
  cups: {
    element: 'Water · Suit of Cups',
    realm: 'Realm of the Heart',
    glyph: Droplets,
    companionGlyph: Heart,
    auraColor: 'from-cyan-400/30 via-rose-400/25 to-blue-500/20',
  },
  wands: {
    element: 'Fire · Suit of Wands',
    realm: 'Realm of Will & Spirit',
    glyph: Flame,
    companionGlyph: Wand2,
    auraColor: 'from-amber-400/35 via-orange-500/25 to-lavender-200/30',
  },
  swords: {
    element: 'Air · Suit of Swords',
    realm: 'Realm of the Mind',
    glyph: Sword,
    companionGlyph: Feather,
    auraColor: 'from-cyan-400/35 via-indigo-500/25 to-lavender-200/25',
  },
  pentacles: {
    element: 'Earth · Suit of Pentacles',
    realm: 'Realm of the Earth',
    glyph: Coins,
    companionGlyph: Flower2,
    auraColor: 'from-amber-400/30 via-emerald-500/25 to-lavender-200/25',
  },
};

/** Suit-themed visuals for Minor Arcana cards without a hand-crafted glyphMap entry. */
const getMinorGlyph = (card: TarotCard): GlyphMetadata | null => {
  if (!card.suit) return null;
  const style = SUIT_STYLE[card.suit];
  return {
    numeral: RANK_NUMERALS[card.number ?? 0] ?? '✦',
    archetype: style.realm,
    element: style.element,
    glyph: style.glyph,
    companionGlyph: style.companionGlyph,
    auraColor: style.auraColor,
    essence: card.keywords[0] ?? 'Inner Wisdom',
  };
};

interface TarotCardVisualProps {
  card: TarotCard;
  className?: string;
}

export const TarotCardVisual: React.FC<TarotCardVisualProps> = ({ card, className = '' }) => {
  const meta = glyphMap[card.id] || getMinorGlyph(card) || defaultGlyph;
  const GlyphIcon = meta.glyph;
  const CompanionIcon = meta.companionGlyph;

  return (
    <div
      className={`relative w-full h-full min-h-[380px] rounded-2xl overflow-hidden select-none bg-gradient-to-b from-[#3A2752] via-plum to-[#241833] flex flex-col justify-between p-5 border border-lavender-200/40 shadow-2xl ${className}`}
      data-testid="tarot-card-visual"
    >
      {/* Background Starlight & Aura */}
      <div className={`absolute inset-0 bg-gradient-to-tr ${meta.auraColor} blur-2xl opacity-60 pointer-events-none`} />

      {/* Decorative Starry Dust SVG Pattern */}
      <div
        className="absolute inset-0 opacity-15 pointer-events-none mix-blend-screen"
        style={{
          backgroundImage: `radial-gradient(1px 1px at 20px 30px, #DCCFF0, rgba(0,0,0,0)),
                            radial-gradient(1.5px 1.5px at 80px 120px, #ffffff, rgba(0,0,0,0)),
                            radial-gradient(1px 1px at 150px 70px, #DCCFF0, rgba(0,0,0,0)),
                            radial-gradient(1.2px 1.2px at 220px 190px, #ffffff, rgba(0,0,0,0)),
                            radial-gradient(1px 1px at 110px 260px, #DCCFF0, rgba(0,0,0,0)),
                            radial-gradient(1.5px 1.5px at 190px 320px, #ffffff, rgba(0,0,0,0))`,
          backgroundSize: '250px 350px',
        }}
      />

      {/* Inner Filigree Border Frame */}
      <div className="absolute inset-2.5 rounded-xl border border-lavender-200/25 pointer-events-none">
        {/* Corner Accents */}
        <span className="absolute -top-1.5 -left-1.5 text-lavender-200 text-[10px] leading-none">✦</span>
        <span className="absolute -top-1.5 -right-1.5 text-lavender-200 text-[10px] leading-none">✦</span>
        <span className="absolute -bottom-1.5 -left-1.5 text-lavender-200 text-[10px] leading-none">✦</span>
        <span className="absolute -bottom-1.5 -right-1.5 text-lavender-200 text-[10px] leading-none">✦</span>
      </div>

      {/* TOP HEADER: Roman Numeral & Element */}
      <div className="relative z-10 flex items-center justify-between text-lavender-200/90 pt-1 px-1">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-lavender-200/60">✦</span>
          <span className="font-fraunces font-bold text-xs tracking-widest text-lavender-200 uppercase">
            {meta.numeral}
          </span>
        </div>
        <span className="text-[11px] uppercase tracking-wider font-lato text-lavender-200/80">
          {meta.element}
        </span>
      </div>

      {/* CENTER GLYPH STAGE */}
      <div className="relative z-10 my-auto flex flex-col items-center justify-center py-4">
        {/* Celestial Sacred Geometry Rings */}
        <div className="relative w-36 h-36 flex items-center justify-center">
          {/* Outer fine circle with dashes */}
          <div className="absolute inset-0 rounded-full border border-lavender-200/20 border-dashed animate-spin-slow pointer-events-none" />

          {/* Middle Ring */}
          <div className="absolute inset-2 rounded-full border border-lavender-200/30 pointer-events-none" />

          {/* Rotated Diamond Frame */}
          <div className="absolute inset-5 rounded-lg border border-lavender-200/40 rotate-45 bg-plum/50 backdrop-blur-sm shadow-[0_0_20px_rgba(220,207,240,0.15)] pointer-events-none" />

          {/* Central Sacred Glyph */}
          <div className="relative z-10 flex flex-col items-center justify-center transform hover:scale-110 transition-transform duration-300">
            <GlyphIcon
              size={44}
              className="text-lavender-200 filter drop-shadow-[0_0_14px_rgba(220,207,240,0.7)] animate-pulse-gentle"
            />
          </div>

          {/* Companion Glyph Flanking Accents */}
          {CompanionIcon && (
            <div className="absolute -bottom-1 -right-1 p-1 rounded-full bg-plum/80 border border-lavender-200/30 text-lavender-200/80">
              <CompanionIcon size={13} />
            </div>
          )}
        </div>

        {/* Archetype Essence Pill */}
        <div className="mt-4 px-3 py-0.5 rounded-full bg-lavender-200/10 border border-lavender-200/25 backdrop-blur-sm">
          <span className="text-[11px] font-lato tracking-widest uppercase text-lavender-200 font-medium">
            {meta.essence}
          </span>
        </div>
      </div>

      {/* BOTTOM FOOTER: Card Name & Archetype */}
      <div className="relative z-10 text-center pb-1">
        {/* Decorative Divider */}
        <div className="flex items-center justify-center gap-2 mb-2 opacity-60">
          <div className="h-px w-10 bg-gradient-to-r from-transparent to-lavender-200" />
          <span className="text-lavender-200 text-[8px]">◆</span>
          <div className="h-px w-10 bg-gradient-to-l from-transparent to-lavender-200" />
        </div>

        <h3 className="font-fraunces font-bold text-white text-base tracking-wide uppercase drop-shadow-md">
          {card.name}
        </h3>
        <p className="text-[11px] text-lavender-200/80 uppercase tracking-widest font-lato mt-0.5">
          {meta.archetype}
        </p>
      </div>
    </div>
  );
};

export default TarotCardVisual;
